import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { editCoveragesSendAnalytics } from '@amfam/policy/change-coverage/feature';
import { ICoverage, Option, Term, VehicleCoverageAnalyticPayLoad } from '@amfam/policy/models';
import { FEATURES, VehicleCoveragesFacade } from '@amfam/policy/vehicle-coverages/data-access';
import { CoverageFilter } from '@amfam/shared/models';
import { CopyService } from '@amfam/shared/utility/shared-services';

@Component({
  selector: 'ds-coverages-info',
  templateUrl: './coverages-info.component.html',
  styleUrls: ['./coverages-info.component.scss'],
  standalone: false
})
export class CoveragesInfoComponent implements OnInit, OnChanges {
  @Input() vehicleDesc: string;
  @Input() coverages: ICoverage[];
  @Input() form: UntypedFormGroup;
  @Input() state: string;
  @Input() selectedId: string;
  @Input() isRecalculate: boolean;
  @Input() isPartner: boolean;
  @Output() updateCoverage = new EventEmitter<{
    selectedId: string;
    coverage: ICoverage;
    term: Term;
    option?: Option;
    value?: string | boolean;
  }>();

  coverageFilter: { [code: string]: CoverageFilter };
  initialized = false;
  initialVechicleCoverageIdsLength = 0;
  currentVechicleCoverageIdsLength = 0;
  //isSendRecalculateANalytics = false;
  public isInitalRender = false;
  public isQouteEnabled = false;
  filterMap: Map<string, { name: string; states: string[] }> = new Map();
  private stop$ = new Subject<void>();
  constructor(
    public copyService: CopyService,
    private store: Store,
    public vehicleCoveragesFacade: VehicleCoveragesFacade
  ) {}
  ngOnInit(): void {
    this.buildControls(this.coverages);

    this.vehicleCoveragesFacade.ids$.pipe(takeUntil(this.stop$)).subscribe(ids => {
      if (!this.initialized) {
        this.initialVechicleCoverageIdsLength = ids.length;
      }
      this.currentVechicleCoverageIdsLength = ids.length;

      if (this.initialVechicleCoverageIdsLength < this.currentVechicleCoverageIdsLength) {
        //this.isSendRecalculateANalytics = false;
      }

      this.initialized = true;
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.initialized) {
      const roadsideAssistanceKey = 'Emergency Roadside Assistance';
      const perviousRoadSideCoverage = changes?.coverages?.previousValue.filter(
        (c: ICoverage) => c.name === roadsideAssistanceKey
      );
      const isQouteEnabled =
        this.initialVechicleCoverageIdsLength < this.currentVechicleCoverageIdsLength;

      for (const coverage of this.coverages) {
        if (!this.isPartner) {
          //this.createCommonControl(coverage);
          if (
            coverage.publicID === 'PAEmergencyRoadServiceCov_af' &&
            perviousRoadSideCoverage?.selected !== undefined &&
            coverage.selected !== perviousRoadSideCoverage.selected
          ) {
            const payload: VehicleCoverageAnalyticPayLoad = {
              event: FEATURES.CHANGE,
              buttonAnalyticName: (isQouteEnabled ? 'Quote:' : '') + 'RoadsideAssist'
            };
            this.store.dispatch(editCoveragesSendAnalytics({ payload }));
          }
        }

        if (this.isCheckbox(coverage)) {
          this.form.controls[coverage.publicID].setValue(coverage.selected);
        } else {
          coverage.terms.forEach(term => {
            this.form.controls[`${coverage.publicID}${term.name}`].setValue(term.chosenTermValue);
          });
        }
      }
    }
    // enable after ng init
    //this.isSendRecalculateANalytics = true;
  }

  isCheckbox(coverage: ICoverage) {
    const chk =
      !coverage.hasTerms ||
      (coverage.hasTerms &&
        coverage.terms &&
        coverage.terms.length > 0 &&
        coverage.terms[0].options.length === 1);
    return chk;
  }

  updateCoverages(
    selectedId: string,
    coverage: ICoverage,
    term: Term,
    value: string | boolean,
    option?: Option
  ) {
    this.updateCoverage.emit({ selectedId, coverage, term, value, option });
    const filterMap: Map<string, CoverageFilter> = this.getCoveragesForState();
    const selectedCoverageConfig = filterMap.get(coverage.publicID);
    if (selectedCoverageConfig) {
      const packagedIn = selectedCoverageConfig.packagedIn;
      if (packagedIn) {
        const packagedInCoverages = this.coverages.filter(c => packagedIn.includes(c.publicID));
        packagedInCoverages.forEach(packagedInCoverage => {
          if (packagedInCoverage && this.isCheckbox(packagedInCoverage)) {
            if (!value) {
              this.updateCoverage.emit({
                selectedId: this.selectedId,
                coverage: packagedInCoverage,
                term: undefined,
                option: undefined,
                value: false
              });
            }
          }
        });
      }

      const linkedTerms = selectedCoverageConfig.linkedTerms;
      if (linkedTerms) {
        const parentTermIds = Object.keys(linkedTerms);
        parentTermIds.forEach(parentTermId => {
          const childTermId = linkedTerms[parentTermId];
          if (childTermId) {
            const linkedCoverage = this.coverages.find(c => c.publicID === coverage.publicID);
            const parentTerm = linkedCoverage?.terms?.find(t => t.publicID === parentTermId);
            if (parentTerm) {
              const selectedOption = parentTerm.options?.find(
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                o => o.name === (option as any)
              )?.code;
              if (selectedOption) {
                const selectedOptionIndex = parentTerm.options?.findIndex(
                  o => String(o.code) === String(selectedOption)
                );
                if (selectedOptionIndex > -1) {
                  const childTerm = linkedCoverage.terms?.find(t => t.publicID === childTermId);
                  if (childTerm) {
                    const childOption = childTerm.options?.find(
                      (ct, i) => i === selectedOptionIndex
                    );
                    if (childOption) {
                      this.updateCoverage.emit({
                        selectedId,
                        coverage: linkedCoverage,
                        term: childTerm,
                        value: null,
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        option: childOption.name as any
                      });
                    }
                  }
                }
              }
            }
          }
        });
      }
    }
  }

  private getCoveragesForState(): Map<string, CoverageFilter> {
    this.coverageFilter = this.copyService.getCopy(
      this.isPartner
        ? 'policy.changeCoverage.partnerCoverageFilter'
        : 'policy.changeCoverage.coverageFilter'
    );
    const filterMap: Map<string, CoverageFilter> = new Map();
    Object.entries(this.coverageFilter).forEach(([key, filterCoverage]) => {
      if (
        filterCoverage &&
        (!filterCoverage.states || filterCoverage.states?.includes(this.state))
      ) {
        filterMap.set(key, filterCoverage);
      }
    });
    return filterMap;
  }

  private buildControls(coverages: ICoverage[]) {
    for (const coverage of coverages) {
      const coveragesConfig = this.getCoveragesForState();
      const coverageConfig = coveragesConfig.get(coverage.publicID);
      if (coverageConfig?.forceCheckbox) coverage.hasTerms = false;

      if (this.isCheckbox(coverage)) {
        this.form.addControl(coverage.publicID, new UntypedFormControl(coverage.selected));
      } else {
        coverage.terms.forEach(term => {
          this.form.addControl(
            coverage.publicID + term.name,
            new UntypedFormControl(term.chosenTermValue)
          );
        });
      }
    }
  }
}
